import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import type { TourStepComponent } from '../../hooks/useCombinedSegments.tsx';
import { CrossIconCloseButton } from '../../common/CrossIconCloseButton.tsx';
import { useSpotlightActions } from '../../hooks/useSpotlightActions.ts';

import { WithMoreActionsMenuOpen } from './WithMoreActionsMenuOpen.tsx';
export const LIVE_DOC_SHARE_BUTTON_TARGET = 'live-doc-share-button-spotlight-target';
export const CONVERT_TO_PAGE_SPOTLIGHT_TARGET = 'convert-to-page-spotlight-target';

const SHARE_MENU_SPOTLIGHT_NAME = 'live-doc-share-button-spotlight';
const CONVERT_TO_PAGE_SPOTLIGHT_NAME = 'convert-to-page-spotlight';

const COMMUNITY_POST_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/create-and-collaborate-in-real-time-with-live-docs/';

const ShareMenuSpotlight: TourStepComponent = ({
	onNext,
	onBack,
	onDismiss,
	progress,
	messageType = 'engagement',
}) => {
	const { Spotlight } = useChoreographerAssets();

	const { formatMessage } = useIntl();

	const { actions, actionsBeforeElement } = useSpotlightActions({
		progress,
		onNext,
		onBack,
		source: SHARE_MENU_SPOTLIGHT_NAME,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();

	return (
		<Spotlight
			actions={actions}
			actionsBeforeElement={actionsBeforeElement}
			dialogPlacement="bottom right"
			dialogWidth={338}
			heading={formatMessage(shareMenuSpotlight.heading)}
			headingAfterElement={
				<CrossIconCloseButton onClose={onDismiss} source={SHARE_MENU_SPOTLIGHT_NAME} />
			}
			key="live-doc-share-button-spotlight"
			messageId="live-doc-share-button-spotlight"
			messageType={messageType}
			shouldWatchTarget
			target={LIVE_DOC_SHARE_BUTTON_TARGET}
			targetBgColor={token('elevation.surface')}
			targetRadius={4}
		>
			{formatMessage(shareMenuSpotlight.body, {
				a: (chunks: React.ReactNode) => (
					<Anchor
						onClick={() => {
							createAnalyticsEvent({
								type: 'sendUIEvent',
								data: {
									source: SHARE_MENU_SPOTLIGHT_NAME,
									action: 'clicked',
									actionSubject: 'link',
									actionSubjectId: 'exploreLiveDocs',
								},
							}).fire();
						}}
						href={COMMUNITY_POST_LINK}
						target="_blank"
						testId="live-doc-share-button-spotlight-link"
						xcss={linkStyles}
					>
						{chunks}
					</Anchor>
				),
			})}
		</Spotlight>
	);
};

const ConvertToPageSpotlight: TourStepComponent = ({
	onNext,
	onBack,
	onDismiss,
	progress,
	messageType = 'engagement',
}) => {
	const { Spotlight } = useChoreographerAssets();

	const { formatMessage } = useIntl();

	const { actions, actionsBeforeElement } = useSpotlightActions({
		progress,
		onNext,
		onBack,
		source: CONVERT_TO_PAGE_SPOTLIGHT_NAME,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();

	return (
		<WithMoreActionsMenuOpen>
			<Spotlight
				actions={actions}
				actionsBeforeElement={actionsBeforeElement}
				dialogPlacement="left top"
				dialogWidth={328}
				header={({ children }: { children: React.ReactNode }) => (
					<Box xcss={headerStyles}>{children}</Box>
				)}
				heading={formatMessage(convertToPageSpotlight.heading)}
				headingAfterElement={
					<CrossIconCloseButton onClose={onDismiss} source={CONVERT_TO_PAGE_SPOTLIGHT_NAME} />
				}
				key={CONVERT_TO_PAGE_SPOTLIGHT_NAME}
				messageId={CONVERT_TO_PAGE_SPOTLIGHT_NAME}
				messageType={messageType}
				shouldWatchTarget
				target={CONVERT_TO_PAGE_SPOTLIGHT_TARGET}
				targetBgColor={token('elevation.surface')}
				targetRadius={4}
			>
				{formatMessage(convertToPageSpotlight.body, {
					a: (chunks: React.ReactNode) => (
						<Anchor
							onClick={() => {
								createAnalyticsEvent({
									type: 'sendUIEvent',
									data: {
										source: CONVERT_TO_PAGE_SPOTLIGHT_NAME,
										action: 'clicked',
										actionSubject: 'link',
										actionSubjectId: 'exploreLiveDocs',
									},
								}).fire();
							}}
							href={COMMUNITY_POST_LINK}
							target="_blank"
							xcss={linkStyles}
						>
							{chunks}
						</Anchor>
					),
				})}
			</Spotlight>
		</WithMoreActionsMenuOpen>
	);
};

const headerStyles = xcss({
	textWrap: 'pretty', // Prevents an orphan in the spotlight headline.

	// These match the default spotlight header styles.
	display: 'flex',
	alignItems: 'baseline',
	justifyContent: 'space-between',
	paddingBlockEnd: 'space.100',
});

const linkStyles = xcss({
	color: 'color.text.inverse',
	textDecoration: 'underline',
	':hover': {
		color: 'color.text.inverse',
	},
	':visited': {
		color: 'color.text.inverse',
		textDecoration: 'underline',
	},
	':active': {
		color: 'color.text.inverse',
		textDecoration: 'underline',
	},
	':focus': {
		color: 'color.text.inverse',
		textDecoration: 'underline',
	},
	':visited:active': {
		color: 'color.text.inverse',
		textDecoration: 'underline',
	},
});

const shareMenuSpotlight = defineMessages({
	heading: {
		id: 'modernize-changeboarding.share-menu-spotlight.heading',
		defaultMessage: 'Open to all when created',
		description: 'Title of onboarding message describing the Live Docs share menu.',
	},
	body: {
		id: 'modernize-changeboarding.share-menu-spotlight.body',
		defaultMessage:
			'When <a>live docs</a> are started, they’re open to all in this space. Make them private anytime.',
		description: 'Body of a tutorial message describing the Live Docs share menu.',
	},
});

const convertToPageSpotlight = defineMessages({
	heading: {
		id: 'modernize-changeboarding.convert-to-page-spotlight.heading',
		defaultMessage: 'Convert any live doc to a page',
		description: 'Title of tutorial message describing the convert to page functionality',
	},
	body: {
		id: 'modernize-changeboarding.convert-to-page-spotlight.body',
		defaultMessage:
			'<a>Live docs</a> can be converted and published as pages (and converted back) anytime.',
		description: 'Body of a tutorial message describing the convert to page menu item',
	},
});

export const Spotlights = {
	ShareMenuSpotlight,
	ConvertToPageSpotlight,
};
